/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 *
 * This file is proprietary and confidential. Unauthorized copying,
 * distribution, modification, or use of this file, via any medium,
 * is strictly prohibited without the express written permission of
 * SKYXOPS Corp.
 * ============================================================================= */
/**
 * AI-LAUNCH-59 — AWS Marketplace registration landing page.
 *
 * Reached only by AWS redirecting a buyer here with a form POST; the backend
 * redeems the token and 303s to this page holding a signed session.
 *
 * WITHOUT a verified registration this page does not exist for you: the visitor
 * is redirected to /login and never sees marketplace-specific content at all.
 * Not a form, and not an inert message either — an explanatory page would still
 * confirm the flow exists to anyone who probed the URL.
 *
 * Shell/theme is deliberately identical to LoginPage so the buyer's first
 * SKYXOPS screen matches the one they'll sign in on.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
} from '@skyxops/ui';
import { ApiError, marketplaceComplete, marketplaceSession } from '../api';

const SKYXOPS_LOGO = 'https://appstatic.skyxops.io/skyxops/SKYXOPS-Logo.png';

type View = 'checking' | 'form' | 'done';

export function MarketplaceRegisterPage() {
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });

  const [view, setView] = useState<View>('checking');
  const [freeTrial, setFreeTrial] = useState(false);
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [fullName, setFullName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    // No verified registration => this page does not exist for you. Go to the
    // ordinary login page rather than showing anything marketplace-specific.
    marketplaceSession()
      .then((s) => {
        if (!alive) return;
        if (!s.resolved) {
          navigate('/login', { replace: true });
          return;
        }
        setFreeTrial(Boolean(s.free_trial));
        setView('form');
      })
      .catch(() => alive && navigate('/login', { replace: true }));
    return () => {
      alive = false;
    };
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await marketplaceComplete({ email, company, full_name: fullName });
      setView('done');
    } catch (err) {
      // 403 means the signed handoff expired or was never there — say so plainly
      // rather than leaving them retrying a form that cannot succeed.
      if (err instanceof ApiError && err.status === 403) setError(t('marketplace.error_session'));
      else setError(t('marketplace.error_generic'));
    } finally {
      setBusy(false);
    }
  }

  const fieldLabel = 'text-sm font-medium text-foreground';

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-white to-violet-50 p-4 dark:from-skyxops-deepMidnight dark:via-skyxops-midnight dark:to-skyxops-ink">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-border/60 bg-card p-8 shadow-xl">
          <div className="mb-7 flex justify-center">
            <img src={SKYXOPS_LOGO} alt="SKYXOPS" className="h-9 w-auto" />
          </div>

          {/* Deliberately renders nothing while the session is checked: a
              visitor being redirected to /login must never glimpse this page. */}
          {view === 'checking' && <div className="h-4" aria-hidden="true" />}

          {/* ---- FORM: a verified buyer. ---- */}
          {view === 'form' && (
            <>
              <div className="mb-6 space-y-1 text-center">
                <h1 className="text-xl font-semibold tracking-tight text-foreground">
                  <FormattedMessage id="marketplace.title" />
                </h1>
                <p className="text-sm text-muted-foreground">
                  <FormattedMessage id={freeTrial ? 'marketplace.subtitle_trial' : 'marketplace.subtitle'} />
                </p>
              </div>

              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertTitle><FormattedMessage id="marketplace.error_title" /></AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={onSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label htmlFor="mkt-email" className={fieldLabel}>
                    <FormattedMessage id="marketplace.label_email" />
                  </label>
                  <Input
                    id="mkt-email" type="email" autoComplete="username" required autoFocus
                    placeholder={t('marketplace.placeholder_email')} disabled={busy}
                    value={email} onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="mkt-company" className={fieldLabel}>
                    <FormattedMessage id="marketplace.label_company" />
                  </label>
                  <Input
                    id="mkt-company" type="text" autoComplete="organization"
                    placeholder={t('marketplace.placeholder_company')} disabled={busy}
                    value={company} onChange={(e) => setCompany(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="mkt-name" className={fieldLabel}>
                    <FormattedMessage id="marketplace.label_name" />
                  </label>
                  <Input
                    id="mkt-name" type="text" autoComplete="name"
                    placeholder={t('marketplace.placeholder_name')} disabled={busy}
                    value={fullName} onChange={(e) => setFullName(e.target.value)}
                  />
                </div>
                        <p className="text-xs text-muted-foreground">
                  <FormattedMessage id="marketplace.invite_hint" />
                </p>
                <Button type="submit" className="w-full" disabled={busy}>
                  <FormattedMessage id={busy ? 'marketplace.submitting' : 'marketplace.submit'} />
                </Button>
              </form>
            </>
          )}

          {/* ---- DONE: created, but NOT yet entitled. ---- */}
          {view === 'done' && (
            <div className="space-y-4 text-center">
              <div className="space-y-1">
                <h1 className="text-xl font-semibold tracking-tight text-foreground">
                  <FormattedMessage id="marketplace.done_title" />
                </h1>
                <p className="text-sm text-muted-foreground">
                  <FormattedMessage id="marketplace.done_body" />
                </p>
              </div>
              <a
                href="/login"
                className="inline-block text-xs font-medium text-skyxops-lilac hover:underline"
              >
                <FormattedMessage id="marketplace.done_signin" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MarketplaceRegisterPage;
